import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import BreadCrumbs from '../../SharedComponents/BreadCrumbs';
import Loading from '../../SharedComponents/Loading';
import Modal from '../../SharedComponents/Modal';
import CheckCircle from '../../SharedComponents/Svg/CheckCircle';
import XCircle from '../../SharedComponents/Svg/XCircle';
import Eye from '../../SharedComponents/Svg/Eye';
import Share from '../../SharedComponents/Svg/Share';
import Trash from '../../SharedComponents/Svg/Trash';
import Search from '../../SharedComponents/Svg/Search';
import ChevronLeft from '../../SharedComponents/Svg/ChevronLeft';
import ChevronRight from '../../SharedComponents/Svg/ChevronRight';
import Photograph from '../../SharedComponents/Svg/Photograph';
import {
  getKhareefPhotos,
  setKhareefPhotoStatus,
  deleteKhareefPhoto,
  errorMessage,
  STATUS,
  STATUS_META,
} from './ApiKhareefPhotos';

const FILTERS = ['All', STATUS.PENDING, STATUS.APPROVED, STATUS.PUBLISHED, STATUS.REJECTED];

// Dashboard summary cards (step 4-6 counts). 'All' is the total, and doubles as
// the way back to the unfiltered list.
const SUMMARY_CARDS = [
  { status: 'All', label: 'All', className: 'bg-gray-50 text-gray-800 border-gray-200' },
  { status: STATUS.PENDING, label: 'Pending', className: 'bg-yellow-50 text-yellow-800 border-yellow-200' },
  { status: STATUS.APPROVED, label: 'Approved', className: 'bg-blue-50 text-blue-800 border-blue-200' },
  { status: STATUS.PUBLISHED, label: 'Published', className: 'bg-green-50 text-green-800 border-green-200' },
  { status: STATUS.REJECTED, label: 'Rejected', className: 'bg-red-50 text-red-800 border-red-200' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'participant_asc', label: 'Participant (A–Z)' },
  { value: 'participant_desc', label: 'Participant (Z–A)' },
];

const PAGE_SIZES = [6, 9, 12, 24];

export default function KhareefPhotosManage() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language !== 'en';

  const [photos, setPhotos] = useState([]);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [pageSize, setPageSize] = useState(9);
  const [page, setPage] = useState(1);

  // Dialog targets
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery(['khareefPhotos'], getKhareefPhotos, {
    refetchOnMount: true,
  });

  // Every mutation re-reads the list so the grid and the cards keep matching
  // what is actually stored.
  const refreshList = () => queryClient.invalidateQueries(['khareefPhotos']);

  useEffect(() => {
    if (data?.data) setPhotos(data.data);
  }, [data]);

  // Any change to the filtering/sorting controls returns to the first page.
  useEffect(() => {
    setPage(1);
  }, [filter, search, sort, pageSize]);

  const statusMutation = useMutation(setKhareefPhotoStatus, {
    onSuccess: ({ data: updated }) => {
      setPhotos((prev) =>
        prev.map((p) =>
          p.id === updated.id
            ? {
                ...p,
                status: updated.status,
                rejectionReason: updated.status === STATUS.REJECTED ? updated.reason : p.rejectionReason,
              }
            : p,
        ),
      );
      toast.success(t(STATUS_META[updated.status].label));
      refreshList();
    },
    onError: (err) => toast.error(t(errorMessage(err)) || t('Something went wrong')),
  });

  const deleteMutation = useMutation(deleteKhareefPhoto, {
    onSuccess: ({ data: removed }) => {
      setPhotos((prev) => prev.filter((p) => p.id !== removed.id));
      setDeleteTarget(null);
      toast.success(t('Photo deleted'));
      refreshList();
    },
    onError: (err) => toast.error(t(errorMessage(err)) || t('Something went wrong')),
  });

  const changeStatus = (id, status) => statusMutation.mutate({ id, status });

  const submitReject = () => {
    if (!rejectReason.trim()) {
      toast.error(t('Please enter a reason for rejection'));
      return;
    }
    statusMutation.mutate(
      { id: rejectTarget.id, status: STATUS.REJECTED, reason: rejectReason.trim() },
      {
        onSuccess: () => {
          setRejectTarget(null);
          setRejectReason('');
        },
      },
    );
  };

  const counts = useMemo(
    () => photos.reduce((acc, p) => ({ ...acc, [p.status]: (acc[p.status] || 0) + 1 }), {}),
    [photos],
  );

  // Filter -> Search -> Sort, all client-side over the fetched list.
  const processed = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = filter === 'All' ? photos : photos.filter((p) => p.status === filter);
    if (term) {
      list = list.filter((p) =>
        [p.participant, p.title, p.location]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(term)),
      );
    }
    const sorted = [...list];
    sorted.sort((a, b) => {
      switch (sort) {
        case 'oldest':
          return (a.submittedAt || '').localeCompare(b.submittedAt || '');
        case 'participant_asc':
          return (a.participant || '').localeCompare(b.participant || '');
        case 'participant_desc':
          return (b.participant || '').localeCompare(a.participant || '');
        case 'newest':
        default:
          return (b.submittedAt || '').localeCompare(a.submittedAt || '');
      }
    });
    return sorted;
  }, [photos, filter, search, sort]);

  const totalPages = Math.max(1, Math.ceil(processed.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visible = processed.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (isLoading) return <Loading />;
  if (isError)
    return <div className="text-red-600">{t(errorMessage(error)) || t('Something went wrong')}</div>;

  return (
    <>
      <header className="flex justify-between items-center mb-4">
        <BreadCrumbs to="/khareef-photos" name={t('Khareef Photos')} />
        <Link
          to="/khareef-photos"
          className="bg-blackv2 hover:bg-blackv1 text-white text-sm rounded px-4 py-2"
        >
          {t('Preview')} — {t('Public Page')}
        </Link>
      </header>

      {/* Dashboard summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        {SUMMARY_CARDS.map((card) => {
          const isAll = card.status === 'All';
          const active = filter === card.status;
          return (
            <button
              key={card.status}
              // Clicking the active status card clears back to All; the All card
              // itself always just clears the filter.
              onClick={() => setFilter(isAll || active ? 'All' : card.status)}
              className={`text-start border rounded-lg px-5 py-4 transition-shadow hover:shadow ${card.className} ${
                active ? 'ring-2 ring-offset-1 ring-blackv2' : ''
              }`}
            >
              <span className="block text-3xl font-extrabold">
                {isAll ? photos.length : counts[card.status] || 0}
              </span>
              <span className="block text-sm font-medium mt-1">{t(card.label)}</span>
            </button>
          );
        })}
      </div>

      <div className="bg-white px-6 py-6 rounded-lg shadow">
        <div className="flex flex-wrap gap-3 justify-between relative items-center mb-6">
          <span className="font-bold text-xl">{t('Submissions List')}</span>

          {/* Search + Sort toolbar */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <span className={`absolute inset-y-0 flex items-center text-gray-400 ${isRtl ? 'right-2' : 'left-2'}`}>
                <Search className="w-4 h-4" />
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('Search by participant, title or location')}
                className={`border rounded py-2 text-sm w-72 max-w-full ${isRtl ? 'pr-8 pl-3' : 'pl-8 pr-3'}`}
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="border rounded py-2 px-2 text-sm text-gray-600"
              aria-label={t('Sort by')}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {t(o.label)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Status filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map((f) => {
            const active = filter === f;
            const label = f === 'All' ? t('All') : t(STATUS_META[f].label);
            const count = f === 'All' ? photos.length : counts[f] || 0;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-sm rounded-full px-4 py-1 border transition-colors ${
                  active
                    ? 'bg-blackv2 text-white border-blackv2'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                }`}
              >
                {label} ({count})
              </button>
            );
          })}
        </div>

        {/* Submissions grid */}
        {visible.length === 0 ? (
          <div className="flex flex-col items-center text-gray-400 py-16">
            <Photograph className="w-12 h-12" />
            <span className="mt-3">
              {search.trim() ? t('No submissions match your search') : t('No submissions found')}
            </span>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((photo) => {
              const meta = STATUS_META[photo.status];
              return (
                <div key={photo.id} className="border border-gray-200 rounded-lg overflow-hidden flex flex-col">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setPreviewPhoto(photo)}
                      className="block w-full"
                      title={t('Image Preview')}
                    >
                      <img src={photo.url} alt={photo.participant} className="w-full h-44 object-cover" />
                    </button>
                    <span
                      className={`absolute top-2 ${isRtl ? 'left-2' : 'right-2'} text-xs font-semibold px-2 py-1 rounded-full ${meta.className}`}
                    >
                      {t(meta.label)}
                    </span>
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <span className="font-bold text-gray-800">{photo.title}</span>
                    <span className="text-sm text-gray-600">{photo.participant}</span>
                    {photo.instagram && (
                      <a
                        href={`https://instagram.com/${photo.instagram}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        dir="ltr"
                        className={`text-sm text-blue-600 hover:underline w-max ${isRtl ? 'self-end' : ''}`}
                        title={t('Open Instagram profile')}
                      >
                        @{photo.instagram}
                      </a>
                    )}
                    <span className="text-sm text-gray-500">{photo.location}</span>
                    <span className="text-xs text-gray-400 mt-1">
                      {t('Submitted at')}: {photo.submittedAt}
                    </span>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-gray-100">
                      <Link
                        to={`/khareef-photos/manage/${photo.id}`}
                        className="flex items-center gap-1 text-sm bg-gray-50 text-gray-700 hover:bg-gray-100 rounded px-3 py-1"
                      >
                        <Eye className="w-4 h-4" /> {t('View Details')}
                      </Link>
                      {(photo.status === STATUS.PENDING || photo.status === STATUS.REJECTED) && (
                        <button
                          onClick={() => changeStatus(photo.id, STATUS.APPROVED)}
                          className="flex items-center gap-1 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded px-3 py-1"
                        >
                          <CheckCircle className="w-4 h-4" /> {t('Approve')}
                        </button>
                      )}
                      {photo.status === STATUS.APPROVED && (
                        <button
                          onClick={() => changeStatus(photo.id, STATUS.PUBLISHED)}
                          className="flex items-center gap-1 text-sm bg-green-50 text-green-700 hover:bg-green-100 rounded px-3 py-1"
                        >
                          <Share className="w-4 h-4" /> {t('Publish')}
                        </button>
                      )}
                      {photo.status === STATUS.PUBLISHED && (
                        <button
                          onClick={() => changeStatus(photo.id, STATUS.APPROVED)}
                          className="flex items-center gap-1 text-sm bg-gray-50 text-gray-700 hover:bg-gray-100 rounded px-3 py-1"
                        >
                          <Eye className="w-4 h-4" /> {t('Review')}
                        </button>
                      )}
                      {photo.status !== STATUS.REJECTED && (
                        <button
                          onClick={() => {
                            setRejectReason('');
                            setRejectTarget(photo);
                          }}
                          className="flex items-center gap-1 text-sm bg-red-50 text-red-700 hover:bg-red-100 rounded px-3 py-1"
                        >
                          <XCircle className="w-4 h-4" /> {t('Reject')}
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteTarget(photo)}
                        className="flex items-center gap-1 text-sm bg-red-50 text-red-700 hover:bg-red-100 rounded px-3 py-1"
                      >
                        <Trash className="w-4 h-4" /> {t('Delete')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {processed.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-100">
            <div className={`flex items-center gap-2 text-sm text-gray-600 ${isRtl ? 'space-x-reverse' : ''}`}>
              <span>{t('Show')}</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border rounded py-1 px-2"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span>{t('entries')}</span>
            </div>

            <p className="text-sm text-gray-700">
              {t('Showing')} <span className="font-medium">{visible.length}</span> {t('of')}{' '}
              <span className="font-medium">{processed.length}</span> {t('results')}
            </p>

            {totalPages > 1 && (
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label={t('Pagination')}>
                <button
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-40 ${
                    isRtl ? 'rounded-r-md' : 'rounded-l-md'
                  }`}
                >
                  {isRtl ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium hover:bg-gray-50 ${
                      currentPage === n ? 'bg-gray-200 text-gray-900' : 'bg-white text-gray-700'
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-40 ${
                    isRtl ? 'rounded-l-md' : 'rounded-r-md'
                  }`}
                >
                  {isRtl ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
              </nav>
            )}
          </div>
        )}
      </div>

      {/* Reject dialog with rejection reason */}
      <Modal
        isOpen={!!rejectTarget}
        setIsOpen={(open) => {
          if (!open) setRejectTarget(null);
        }}
        title={t('Reject Photo')}
        confirmTitle={t('Reject')}
        onSubmit={submitReject}
        submitType="button"
        isLoading={statusMutation.isLoading}
      >
        {rejectTarget && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              {t('You are rejecting the submission by')} <span className="font-semibold">{rejectTarget.participant}</span>.
            </p>
            <label className="block text-sm font-medium text-gray-700">{t('Rejection Reason')}</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              className="w-full border rounded p-2 text-sm"
              placeholder={t('Enter the reason for rejecting this photo')}
            />
          </div>
        )}
      </Modal>

      {/* Delete confirmation dialog */}
      <Modal
        isOpen={!!deleteTarget}
        setIsOpen={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t('Delete Photo')}
        confirmTitle={t('Delete')}
        onSubmit={() => deleteMutation.mutate(deleteTarget.id)}
        submitType="button"
        isLoading={deleteMutation.isLoading}
      >
        {deleteTarget && (
          <p className="text-sm text-gray-600">
            {t('Are you sure you want to delete this photo? This action cannot be undone.')}
          </p>
        )}
      </Modal>

      {/* Image preview dialog */}
      <Modal
        isOpen={!!previewPhoto}
        setIsOpen={(open) => {
          if (!open) setPreviewPhoto(null);
        }}
        title={previewPhoto ? previewPhoto.title : t('Image Preview')}
        cancelTitle={t('Close')}
        hideSubmit
        className="sm:max-w-3xl"
      >
        {previewPhoto && (
          <div className="space-y-3">
            <img
              src={previewPhoto.url}
              alt={previewPhoto.participant}
              className="w-full max-h-[70vh] object-contain rounded"
            />
            <div className="text-sm text-gray-600">
              <span className="font-semibold">{previewPhoto.participant}</span> — {previewPhoto.location}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
